// MOVE MONEY v2 — batch 3's rebuild of TreasuryRebalance (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on, at
// /treasury/rebalance and /treasury/chain-swap).
//
// Astra's anatomy (02-screen-review/move), the operator's light ruling of 26 Oct: one card — From and To side by side,
// the amount with its asset, "More" folded (the network fee, the date, notes), and the footer's one primary, "Review
// move", which turns into the readback line and the "Move money" confirm. From lg the From account's balance sits
// beside the card, with the Last 10 moves under it again (LastTen, restored 1 Nov 2026); the phone has one "Last 10 →" link.
//
// THE STATE MACHINE IS TODAY'S, COPIED (screens/TreasuryRebalance.tsx): the URL prefill read once (?rail&railAsset,
// ?to&toAsset, ?amount, ?fee, ?when, ?note), the desk-change reset, To cleared when From's asset changes, toAccounts
// (imported), the amount / fee / back-date checks, `ready`, the repeat guard, the reference minted once and held across
// a retry, the transfer body (fee_payer 'house' with no fee), effective_at, the chain-sync nudge, the posted / deduped
// line, ServerLine, RepeatPanel. Only the JSX and the review step are new.
import { useState, useMemo, useEffect, useRef } from 'react';
import { useDesk, DESK_LABEL } from '../../components/DeskSwitch';
import { isSentinel } from '../../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { tg } from '../../lib/telegram';
import { treasuryPath, makeReference, DEFAULT_RAILS } from '../../lib/workflow';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { RepeatPanel, useRepeatGuard } from '../../components/RepeatPanel';
import { MoreDetails } from '../../components/MoreDetails';
import { Field, FieldRow } from '../../components/Field';
import { ErrorLine } from '../../components/ErrorLine';
import { Fact, ReadFailed, ServerLine, StateLine, TAP } from '../../components/v2/kit';
import { amountOk, amountProblem, optionalAmountProblem } from '../../lib/amount';
import { AsOfChips } from '../../components/AsOfChips';
import { displayAsset, fmtAmount, fmtDate, fmtTime, dubaiWallToIso, backdateProblem } from '../../lib/format';
import { railName, sameAsset } from '../../lib/naming';
import { readState } from '../../lib/read-state';
import { toAccounts } from '../TreasuryRebalance';
import { Receipt, ReceiptAmount } from '../../components/v2/Receipt';
import { LastTen, LastTenLink, lastTenHref, type LastTenProps } from '../../components/v2/record/LastTen';
import { AboutJob, FormFoot, FormFrame, PRIMARY, QUIET } from '../../components/v2/move/FormFrame';

const LAST_TEN_MOVES: LastTenProps = { kind: 'moves' };

type FeePayer = 'sender' | 'receiver' | 'house';

/** The two payers a typed fee can have ('house' records NO fee — the 3 Sept audit). The values are the API's. */
const FEE_PAYERS: ReadonlyArray<{ v: Exclude<FeePayer, 'house'>; l: string }> = [
  { v: 'sender',   l: 'From account pays' },
  { v: 'receiver', l: 'To account pays'   },
];

/** One trigger height for both pickers (21 Oct 2026 audit): a picked and an empty RailPicker differ in height. */
const PICKER_HEIGHT = '[&_button.input]:min-h-[42px] lg:[&_button.input]:min-h-[40px]';

export function MoveV2() {
  const nav = useNavigate();

  // ── Today's state, verbatim (TreasuryRebalance.tsx) ────────────────────────────────────────────────────────────
  const [search] = useSearchParams();
  const prefill = ((): RailValue | null => {
    const slug = search.get('rail'), asset = search.get('railAsset');
    return slug && asset ? { slug, asset } : null;
  })();
  const prefillTo = ((): RailValue | null => {
    const slug = search.get('to'), asset = search.get('toAsset');
    return slug && asset ? { slug, asset } : null;
  })();
  const urlFee  = search.get('fee') ?? '';
  const urlWhen = search.get('when') ?? '';
  const [fromRail, setFromRail] = useState<RailValue>(prefill ?? { ...DEFAULT_RAILS.cash });
  const [toRail,   setToRail]   = useState<RailValue>(prefillTo ?? { slug: '', asset: '' });
  // Which desk — DubaiOTC only since the IndiaOTC wind-down; the switch is not drawn (verification 1.4), the state stays.
  const [entity] = useDesk();
  const [amount, setAmount] = useState(search.get('amount') ?? '');
  const [fee, setFee] = useState(urlFee);
  const [feePayer, setFeePayer] = useState<FeePayer>('sender');
  const [notes, setNotes] = useState(search.get('note') ?? '');
  const [when, setWhen] = useState(urlWhen);
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];
  const accounts = readState(treasury);

  const toRows = useMemo(
    () => toAccounts(rows, fromRail, toRail),
    [rows, fromRail, toRail],
  );

  // Switching desk empties both pickers — on a CHANGE only (React's adjust-state-on-prop-change pattern).
  const [prevEntity, setPrevEntity] = useState(entity);
  if (entity !== prevEntity) {
    setPrevEntity(entity);
    setFromRail(entity === 'dubaiotc' ? { ...DEFAULT_RAILS.cash } : { slug: '', asset: '' });
    setToRail({ slug: '', asset: '' });
  }

  // If operator changes From and the current To no longer matches, clear it.
  useEffect(() => {
    if (toRail.asset && fromRail.asset && !sameAsset(toRail.asset, fromRail.asset)) {
      setToRail({ slug: '', asset: '' });
    }
  }, [fromRail.asset]);   // eslint-disable-line react-hooks/exhaustive-deps

  const feeNum = Number(fee) || 0;
  const amountWords = amountProblem(amount);
  const feeWords    = optionalAmountProblem(fee);

  const sameRail = fromRail.slug === toRail.slug
                 && sameAsset(fromRail.asset, toRail.asset)
                 && fromRail.slug !== '';

  const nameOf = (v: RailValue) =>
    railName(rows.find((r) => r.slug === v.slug && sameAsset(r.asset, v.asset)) ?? { slug: v.slug });

  const ready = amountOk(amount) && feeWords === null && whenWords === null
             && !!fromRail.slug && !!toRail.slug && !sameRail;
  // Why Review is off, in a few words (Record's rule; Screens' audit, 27 Oct 2026). Same-account, the fee and the date say
  // their own words on the form.
  const missing = !fromRail.slug ? 'Pick the From account.'
    : !toRail.slug ? 'Pick the To account.'
    : !amountOk(amount) ? 'Enter an amount.'
    : feeWords !== null ? 'Check the fee.'
    : whenWords !== null ? 'Check the date.'
    : null;

  const { twin: repeat, clearTwin, guard } = useRepeatGuard();

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY: minted on the first submit, held in a ref across
  // a retry and a field edit, cleared on success.
  const refRef = useRef<string | null>(null);
  const [posted, setPosted] = useState<{ reference: string; deduped: boolean; amount: number; asset: string; from: string; to: string } | null>(null);
  const submit = useMutation({
    mutationFn: async (opts?: { confirmRepeat?: boolean }) => {
      refRef.current ??= makeReference('REBAL');
      const reference = refRef.current;
      const data = await guard(() => api.transfer({
        entity,
        source:      treasuryPath(fromRail.slug, fromRail.asset, entity),
        destination: treasuryPath(toRail.slug,   toRail.asset,   entity),
        amount:      { asset: fromRail.asset, amount: Number(amount) },
        fee_payer:   feeNum > 0 ? feePayer : 'house',
        fee:         feeNum > 0 ? { asset: fromRail.asset, amount: feeNum } : undefined,
        fee_account: `@expense:${entity}:network_fee`,
        reference,
        notes:       notes || undefined,
        confirm_repeat: opts?.confirmRepeat || undefined,
        effective_at: whenIso || undefined,
      }));
      return { data, reference, amount: Number(amount), asset: fromRail.asset, from: nameOf(fromRail), to: nameOf(toRail) };
    },
    onSuccess: ({ data, reference, ...facts }) => {
      clearTwin();
      refRef.current = null;
      setPosted({ reference: data.reference ?? reference, deduped: !!data.deduped, ...facts });
      setAmount(''); setFee(''); setNotes(''); setWhen('');
      tg()?.HapticFeedback.notificationOccurred('success');
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  // ── The review step (Astra's "Review move"): the readback, then the one confirm. Any edit goes back to the form. ──
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); },
    [fromRail.slug, fromRail.asset, toRail.slug, toRail.asset, amount, fee, feePayer, notes, when, entity]);
  const confirming = reviewing && ready;

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what MOVED, from what posted. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title="Moved"
      deduped={posted.deduped}
      movements="/movements?kind=rail_move"
      reference={posted.reference}
      facts={[
        { label: 'Amount', value: <ReceiptAmount value={posted.amount} asset={posted.asset} /> },
        { label: 'From', value: posted.from },
        { label: 'To', value: posted.to },
      ]}
      another={{ label: 'Move another', onClick: () => submit.reset() }}
      links={!posted.deduped && <Link to={lastTenHref(LAST_TEN_MOVES)} className={TAP}>Recent moves →</Link>}
    />
  ) : null;
  // The Last 10 moves beside the form (restored 1 Nov 2026); after a posting it is all the aside keeps (Astra).
  const lastTenList = <LastTen {...LAST_TEN_MOVES} title="Last 10 moves" />;

  const help = (
    <>
      <p>One desk account to another — Office cash to a bank, the hot wallet to an exchange. No party is involved. Both
        accounts hold the same asset and belong to the desk.</p>
      <p>The same coin on another chain is two moves. A network fee leaves the account you name and is booked as a cost.</p>
    </>
  );
  // The context — the desk, when the accounts were read — sits in the fold with the explanation; the From balance is
  // said once, under its picker (Astra, 1 Nov 2026).
  const facts = [
    { label: 'Desk', value: DESK_LABEL[entity] },
    ...(accounts === 'known' ? [{ label: 'Read', value: fmtTime(treasury.dataUpdatedAt) }] : []),
  ];
  // The phone's way to the job's explanation (the aside is laptop-only): behind a tap (restored 1 Nov 2026).
  const phoneAbout = <AboutJob help={help} facts={facts} className="xl:hidden" />;

  return (
    <FormFrame
      title="Move money"
      below={receipt ? undefined : <div><LastTenLink {...LAST_TEN_MOVES} title="Last 10 moves" />{phoneAbout}</div>}
      aside={receipt ? lastTenList : <div className="space-y-6">
        {lastTenList}
        <AboutJob help={help} facts={facts} />
      </div>}
    >
      {receipt ?? (<>
      {/* ON REVIEW THE FIELDS GIVE WAY to the readback (1 Nov 2026 audit); Edit brings them back, as typed. */}
      {!confirming && (<>
      <FieldRow>
        <Field label="From" className={PICKER_HEIGHT}>
          <RailPicker balanceBelow
            value={fromRail}
            onChange={setFromRail}
            rows={rows}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
          />
        </Field>
        <Field label="To" className={PICKER_HEIGHT}>
          <RailPicker balanceBelow
            value={toRail}
            onChange={setToRail}
            rows={toRows}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
          />
        </Field>
      </FieldRow>

      {/* The asset is From's — named once, in the label; it is not a second input. */}
      <FieldRow>
        <Field label={fromRail.asset ? `Amount in ${displayAsset(fromRail.asset)}` : 'Amount'}>
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
      </FieldRow>

      {/* MORE — the network fee, the date and notes. Open from the start when the URL brought a fee or a time, so the
          figure and the day that will post are on screen. */}
      <div className="border-t border-stroke-soft pt-3">
        <MoreDetails label="More" defaultOpen={urlFee !== '' || urlWhen !== ''}>
          <Field label="Network fee">
            <input className="input" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0" inputMode="decimal" />
            <ErrorLine>{feeWords}</ErrorLine>
            {feeNum > 0 && (
              <div className="mt-2 flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel lg:p-1">
                {FEE_PAYERS.map((p) => (
                  <button key={p.v} type="button" onClick={() => setFeePayer(p.v)}
                    className={`seg whitespace-nowrap text-body lg:text-meta ${feePayer === p.v ? 'seg-on' : ''}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <AsOfChips preset={urlWhen || undefined} value={when} onChange={setWhen} disabled={submit.isPending} />
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </MoreDetails>
      </div>
      </>)}

      {/* THE READBACK — what will post, in the accounts' names, each fact once; the date when it is not now. */}
      {/* The accounts read, said in editing and on Review alike (Review puts the pickers away). */}
      {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} />}

      {/* One fact per row (1 Nov 2026 audit: the amount, both accounts, the fee and the day were one sentence). */}
      {confirming && (
        <div>
          <h2 className="mb-1 text-body font-semibold">Before confirming</h2>
          <Fact label="Amount"><ReceiptAmount value={Number(amount)} asset={fromRail.asset} /></Fact>
          <Fact label="From">{nameOf(fromRail)}</Fact>
          <Fact label="To">{nameOf(toRail)}</Fact>
          {feeNum > 0 && <Fact label="Network fee"><ReceiptAmount value={feeNum} asset={fromRail.asset} /></Fact>}
          {feeNum > 0 && <Fact label="Fee paid by">{feePayer === 'sender' ? 'From account' : 'To account'}</Fact>}
          {whenIso && <Fact label="As of">{fmtDate(whenIso)}</Fact>}
          {notes.trim() !== '' && <Fact label="Notes">{notes.trim()}</Fact>}
        </div>
      )}

      <FormFoot note={sameRail ? <ErrorLine>From and To must differ</ErrorLine> : missing && !submit.isPending ? missing : null}>
        {confirming ? (
          <>
            <button type="button" className={QUIET} disabled={submit.isPending} onClick={() => setReviewing(false)}>Edit</button>
            <button className={PRIMARY} disabled={submit.isPending || !ready} onClick={() => submit.mutate({})}>
              {submit.isPending ? 'Booking…' : 'Move money'}
            </button>
          </>
        ) : (
          <button className={PRIMARY} disabled={submit.isPending || !ready} onClick={() => setReviewing(true)}>
            Review move →
          </button>
        )}
      </FormFoot>

      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}
      </>)}
    </FormFrame>
  );
}
